import { Emitter as EventEmitter } from "strict-event-emitter"


import { ConnectionStatus } from "@/lib/client/types.ts"
import { getLogger } from "@/lib/logging.ts"
import { maybeSetTimeout } from "@/lib/std.ts"

import type { RequestPacket, ResponsePacket } from "@/lib/client/types.ts"

export const WEBSOCKET_REQUEST_DELAY_MS = -1 // Set to a non-negative value to simulate network slowness

export class TokenInfo {
  constructor(readonly accessToken: string, readonly expiresAt: number) {
  }

  expired(offset = 1000 * 5) {
    return !this.expiresAt || this.expiresAt < (Date.now() + offset)
  }

  expiring() {
    return this.expired(1000 * 60 * 3)
  }

  valid() {
    return Boolean(this.accessToken)
  }

  static Empty() {
    return new TokenInfo("", 0)
  }
}

export interface TokenSource {
  token: () => Promise<TokenInfo>
  onTokenChanged: (callback: (token: TokenInfo) => void) => void
}

type Events = {
  status: [ConnectionStatus]
  message: [ResponsePacket]
}

const log = getLogger("WebSocketStableConnection")

export class WebSocketStableConnection extends EventEmitter<Events> {
  private ws: WebSocket | null = null
  private currentStatus: ConnectionStatus = ConnectionStatus.Connecting
  private reconnectAttempt: number = 0
  private reconnectTimeout: number | null = null

  constructor(private readonly baseUrl: string, private readonly tokenSource: TokenSource) {
    super()
    void this.connect()
  }

  private async connect() {
    try {
      const token = await this.tokenSource.token()
      if (!token || !token.valid() || token.expired()) {
        return this.setStatus(ConnectionStatus.AuthFailed)
      }

      const url = this.baseUrl
      log.info("Connecting to:", url)
      this.ws = new WebSocket(url)

      this.ws.onopen = () => {
        log.debug("WebSocket connection established, sending handshake")
        this.sendHandshake(token)
        this.reconnectAttempt = 0
      }

      this.ws.onmessage = (event: MessageEvent) => {
        try {
          const packet = JSON.parse(event.data) as ResponsePacket
          log.debug("← Received:", packet)
          if (packet.id == 0 && packet.path == "wsInitPath") {
            if (packet.failure == null) {
              log.info("WebSocket connection authenticated")
              this.setStatus(ConnectionStatus.Connected)
            } else {
              log.warn("WebSocket connection failed:", packet.failure)
              if (packet.failure.authenticationError != null) {
                this.setStatus(ConnectionStatus.AuthFailed)
              } else {
                this.setStatus(ConnectionStatus.ConnectFailed)
              }
              this.ws?.close()
            }
          } else {
            this.emit("message", packet)
          }
        } catch (e) {
          log.error("Failed to parse WebSocket message:", e)
        }
      }

      this.ws.onclose = () => {
        log.info("WebSocket connection closed")
        if (this.status !== ConnectionStatus.AuthFailed) {
          this.scheduleReconnect()
        }
        if (this.status === ConnectionStatus.Connected) {
          this.setStatus(ConnectionStatus.Closed)
        }
      }

      this.ws.onerror = error => {
        log.error("WebSocket error:", error)
        if (this.status !== ConnectionStatus.Connected) {
          this.setStatus(ConnectionStatus.ConnectFailed)
        }
        this.ws?.close()
      }

    } catch (error) {
      log.error("Connection error:", error)
      this.setStatus(ConnectionStatus.ConnectFailed)
      this.ws?.close()
    }
  }

  private sendHandshake(token: TokenInfo) {
    const handshakeMessage = {
      id: 0,
      path: "wsInitPath",
      payload: {
        token: token.accessToken,
        session: "",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        client: "Matter"
      }
    }

    this.send(handshakeMessage)
  }

  private scheduleReconnect() {
    if (this.reconnectTimeout !== null) {
      window.clearTimeout(this.reconnectTimeout)
    }

    const delay = Math.min(1000 * Math.pow(1.5, this.reconnectAttempt), 30000)
    this.reconnectAttempt++

    log.info(`Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempt})`)
    this.reconnectTimeout = window.setTimeout(() => {
      void this.connect()
    }, delay)
  }

  private setStatus(status: ConnectionStatus) {
    this.currentStatus = status
    this.emit("status", status)
  }

  public get status(): ConnectionStatus {
    return this.currentStatus
  }

  public send(data: RequestPacket) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      log.debug("→ Sending:", data)
      maybeSetTimeout(() => {
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify(data))
        } else {
          log.warn("WebSocket closed during delay, message not sent:", data)
        }
      }, WEBSOCKET_REQUEST_DELAY_MS)
    } else {
      log.warn("Attempted to send message while WebSocket is not connected:", data)
    }
  }
}
